/**
 * Utility functions for OpenAI API interactions
 */

/**
 * Extracts JSON from OpenAI response, removing markdown code blocks if present
 */
export const extractJSON = (response: string): any => {
  let cleaned = response.trim();

  // Remove markdown code blocks if present
  cleaned = cleaned.replace(/```json\n?/g, '');
  cleaned = cleaned.replace(/```\n?/g, '');

  try {
    return JSON.parse(cleaned);
  } catch {
    console.error('Failed to parse JSON from OpenAI response:', cleaned);
    throw new Error('Invalid JSON response from OpenAI');
  }
};

/**
 * Validates that translated JSON has the same keys as the original
 */
export const validateTranslationKeys = (
  original: Record<string, string>,
  translated: Record<string, string>
): { valid: boolean; missingKeys: string[]; extraKeys: string[] } => {
  const originalKeys = new Set(Object.keys(original));
  const translatedKeys = new Set(Object.keys(translated));

  const missingKeys = Array.from(originalKeys).filter(
    key => !translatedKeys.has(key)
  );
  const extraKeys = Array.from(translatedKeys).filter(
    key => !originalKeys.has(key)
  );

  return {
    valid: missingKeys.length === 0 && extraKeys.length === 0,
    missingKeys,
    extraKeys,
  };
};

/**
 * Estimates token count (rough approximation)
 */
export const estimateTokens = (text: string): number => {
  // Rough estimation: 1 token ≈ 4 characters
  return Math.ceil(text.length / 4);
};

/**
 * Checks if the translation request is within token limits
 */
export const isWithinTokenLimit = (
  translations: Record<string, string>,
  maxTokens: number = 8000
): boolean => {
  const jsonString = JSON.stringify(translations);
  const estimatedTokens = estimateTokens(jsonString);
  return estimatedTokens < maxTokens;
};

/**
 * Splits large translation objects into smaller chunks if needed
 */
export const chunkTranslations = (
  translations: Record<string, string>,
  maxTokensPerChunk: number = 6000
): Record<string, string>[] => {
  const entries = Object.entries(translations);
  const chunks: Record<string, string>[] = [];
  let currentChunk: Record<string, string> = {};
  let currentTokens = 0;

  for (const [key, value] of entries) {
    const entryTokens = estimateTokens(JSON.stringify({ [key]: value }));

    if (
      currentTokens + entryTokens > maxTokensPerChunk &&
      Object.keys(currentChunk).length > 0
    ) {
      chunks.push(currentChunk);
      currentChunk = {};
      currentTokens = 0;
    }

    currentChunk[key] = value;
    currentTokens += entryTokens;
  }

  if (Object.keys(currentChunk).length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
};
